import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp } from 'antd'
import { createSession, listSessions } from '../../api/chat'
import { Composer, pickAgent, useAgents, useAllScopeKbs } from './Composer'
import { Answer } from './Answer'
import { loadPrefs, savePrefs, scopeToRequest, type ChatPrefs, type Scope } from './prefs'
import { useChat } from './useChat'
import { useQuestionRouter } from './useRoute'
import { useThinkMode, type ThinkCarry } from '../think/useThinkMode'
import { useResearchMode } from '../research/useResearchMode'
import './chat.css'

/** 从新对话页带到对话页的“第一句话”。 */
interface Pending {
  query: string
  prefs: ChatPrefs
}

function usePrefs(initialScope?: Scope) {
  const [prefs, setPrefs] = useState<ChatPrefs>(() => {
    const p = loadPrefs()
    return initialScope ? { ...p, scope: initialScope } : p
  })
  const update = (p: ChatPrefs) => {
    setPrefs(p)
    savePrefs(p)
  }
  return [prefs, update] as const
}

/** 新对话：一个输入框。按下「思考讨论」，同一个框就变成要决定的问题。 */
export function NewChatPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { message } = AntApp.useApp()
  const carried = location.state as ({ scope?: Scope; mode?: 'think' | 'research' } & Omit<ThinkCarry,'mode'>) | null
  const [prefs, setPrefs] = usePrefs(carried?.scope)
  const [starting, setStarting] = useState(false)
  const think = useThinkMode(carried?.mode==='research'?{...carried,mode:undefined}:carried as ThinkCarry|null)
  const research = useResearchMode()
  useEffect(()=>{if(carried?.mode==='research')research.onToggle(true)},[])
  // 思考讨论和深入研究只能开一个
  const thinkMode = { ...think.mode, onToggle: (on: boolean) => (think.mode.onToggle(on), on && research.onToggle(false)) }
  const researchMode = { on: research.on, onToggle: (on: boolean) => (research.onToggle(on), on && think.mode.onToggle(false)) }

  const start = async (query: string) => {
    setStarting(true)
    try {
      const session = await createSession()
      navigate(`/chat/${session.id}`, { state: { pending: { query, prefs } satisfies Pending } })
    } catch (e) {
      message.error(e instanceof Error ? e.message : '没能开始对话')
      setStarting(false)
    }
  }

  return (
    <div className="newchat">
      <div className="newchat-inner">
        <h1>今天想弄懂什么？</h1>
        <Composer
          prefs={prefs}
          onPrefs={setPrefs}
          onSend={(q) => (think.mode.on ? think.start(q, prefs.scope) : research.on ? research.start(q, prefs.scope) : void start(q))}
          busy={think.mode.on ? think.starting : research.on ? research.starting : starting}
          size="lg"
          autoFocus
          think={thinkMode}
          research={researchMode}
          initialText={think.initialText}
          onText={think.onText}
        />
      </div>
    </div>
  )
}

export function ChatPage() {
  const { sessionId = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const allKbs = useAllScopeKbs()
  const agents = useAgents()
  const [prefs, setPrefs] = usePrefs()
  const { turns, history, busy, send, stop } = useChat(sessionId)
  // 答完觉得要拿主意：同一个框按「思考讨论」
  const think = useThinkMode()
  const research = useResearchMode()
  useEffect(() => {
    think.mode.onToggle(false)
    research.onToggle(false)
  }, [sessionId])
  const thinkMode = { ...think.mode, onToggle: (on: boolean) => (think.mode.onToggle(on), on && research.onToggle(false)) }
  const researchMode = { on: research.on, onToggle: (on: boolean) => (research.onToggle(on), on && think.mode.onToggle(false)) }
  const sent = useRef<string | null>(null)
  const end = useRef<HTMLDivElement>(null)

  const title = useQuery({ queryKey: ['sessions'], queryFn: () => listSessions(1, 30) }).data?.data.find((s) => s.id === sessionId)?.title

  const allIds = (allKbs ?? []).map((k) => k.id)
  const route = useQuestionRouter(allIds)

  /** wide：不缩小范围（「改查全部资料」）。 */
  const ask = (query: string, p: ChatPrefs = prefs, wide = false) => {
    const req = scopeToRequest(p.scope, allIds)
    // 「不查资料」不带智能体：带了的话灵智原生会退回去查智能体配好的库
    const agent = p.scope.kind === 'none' ? undefined : pickAgent(agents, p.agentId)
    // 问题里说了在哪儿找（我写的、某个分区）：只在那几篇里找
    const prepare = !wide && (p.scope.kind === 'all' || p.scope.kind === 'kb') ? () => route(query, req.knowledgeBaseIds).then((r) => r && { knowledgeBaseIds: r.knowledgeBaseIds, knowledgeIds: r.knowledgeIds, route: r.label }) : undefined
    return send({ query, ...req, agent, modelId: p.modelId, prepare })
  }

  // 新对话页带来的第一句话：只发一次（StrictMode 下也只发一次），发完把它从地址状态里清掉
  useEffect(() => {
    const pending = (location.state as { pending?: Pending } | null)?.pending
    if (!pending || !allKbs || sent.current === sessionId) return
    sent.current = sessionId
    setPrefs(pending.prefs)
    navigate(location.pathname + location.search, { replace: true, state: null })
    void ask(pending.query, pending.prefs)
  }, [sessionId, allKbs])

  // 新内容出来时滚到底
  const last = turns[turns.length - 1]
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' })
  }, [turns.length, last?.text.length])

  useEffect(() => {
    if (history.isError) qc.invalidateQueries({ queryKey: ['sessions'] })
  }, [history.isError, qc])

  return (
    <div className="chat">
      <header className="chat-top">
        <h1>{title || '对话'}</h1>
      </header>
      <div className="thread" aria-label="对话记录">
        {history.isError && <p className="empty-chat">没能载入这个对话：{history.error.message}</p>}
        {!history.isPending && !history.isError && turns.length === 0 && !busy && <p className="empty-chat">从下面开始提问</p>}
        {turns.map((t, i) => {
          if (t.role === 'user') return <div key={t.key} className="turn-user">{t.text}</div>
          const question = turns[i - 1]?.role === 'user' ? turns[i - 1].text : ''
          return (
            <Answer
              key={t.key}
              turn={t}
              onWiden={question && !busy ? () => void ask(question, prefs, true) : undefined}
              onResearch={question && !research.starting ? () => research.start(question, prefs.scope) : undefined}
            />
          )
        })}
        <div ref={end} />
      </div>
      <div className="dock">
        <Composer
          key={sessionId}
          prefs={prefs}
          onPrefs={setPrefs}
          onSend={(q) => (think.mode.on ? think.start(q, prefs.scope) : research.on ? research.start(q, prefs.scope) : void ask(q))}
          onStop={stop}
          busy={think.mode.on ? think.starting : research.on ? research.starting : busy}
          placeholder="继续问…"
          think={thinkMode}
          research={researchMode}
          onText={think.onText}
        />
      </div>
    </div>
  )
}
